import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Loading from '../../../components/otros/Loading';
import DataTable from 'react-data-table-component';
import AdminSidebar from '../../../components/nav/AdminSidebar';

import { getUsers } from '../../../functions/api/auth';
import { getActivosCol } from '../../../functions/api/activos';
import { getAsignaciones, createAsignaciones } from '../../../functions/api/asignacionNaves';
import { toast } from 'react-toastify';

const AsignarNave = () => {
    const [loading, setLoading] = useState(true);
    const [usuarios, setUsuarios] = useState([]);
    const [naves, setNaves] = useState([]);
    const [asignaciones, setAsignaciones] = useState([]);
    const [userSelected, setUserSelected] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNavesIds, setSelectedNavesIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [tableFilter, setTableFilter] = useState('');

    const { user } = useSelector((state) => state);

    const fetchAsignaciones = useCallback(async () => {
        const asignacionesRes = await getAsignaciones(user.token);
        setAsignaciones(asignacionesRes.data);
    }, [user.token]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const usersRes = await getUsers(user.token);
                setUsuarios(usersRes.data);
                const navesRes = await getActivosCol(user.token);
                setNaves(navesRes.data);
                await fetchAsignaciones();
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [user.token, fetchAsignaciones]);

    const openModal = (userId) => {
        setUserSelected(String(userId));
        const userNaves = asignaciones
            .filter((a) => String(a.userid) === String(userId))
            .map((a) => String(a.activo_id));
        setSelectedNavesIds(userNaves);
        setSearchTerm('');
        setIsModalOpen(true);
    };

    const handleCheckboxChange = (activoId, checked) => {
        setSelectedNavesIds((prev) => {
            if (checked) {
                return prev.includes(activoId) ? prev : [...prev, activoId];
            } else {
                return prev.filter((id) => id !== activoId);
            }
        });
    };

    const handleSelectAll = () => {
        const filteredIds = filteredNaves.map((n) => String(n.id));
        const allSelected = filteredIds.every((id) => selectedNavesIds.includes(id));
        if (allSelected) {
            setSelectedNavesIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
        } else {
            setSelectedNavesIds((prev) => [...new Set([...prev, ...filteredIds])]);
        }
    };

    const saveValues = async () => {
        try {
            const payload = selectedNavesIds.length > 0
                ? selectedNavesIds.map((activoId) => ({ userid: userSelected, activo_id: activoId }))
                : [{ userid: userSelected, activo_id: null }];

            await createAsignaciones(user.token, payload);
            toast.success('Naves asignadas con éxito');

            await fetchAsignaciones();
            setUserSelected(null);
            setSelectedNavesIds([]);
            setIsModalOpen(false);
        } catch (err) {
            console.error('Error saving asignaciones:', err);
            toast.error('Error al guardar las asignaciones');
        }
    };

    const closeModal = () => {
        setUserSelected(null);
        setSelectedNavesIds([]);
        setSearchTerm('');
        setIsModalOpen(false);
    };

    const filteredNaves = useMemo(() => {
        if (!searchTerm.trim()) return naves;
        const term = searchTerm.toLowerCase();
        return naves.filter(
            (n) =>
                n.nombre_activo?.toLowerCase().includes(term) ||
                n.tipo_activo?.toLowerCase().includes(term)
        );
    }, [naves, searchTerm]);

    const tableCustomStyles = {
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
                fontWeight: '600',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6c757d',
                minHeight: '38px',
            },
        },
        headCells: {
            style: {
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        rows: {
            style: {
                fontSize: '0.82rem',
                color: '#212529',
                minHeight: '42px',
                borderBottom: '1px solid #f1f3f5',
                '&:hover': {
                    backgroundColor: '#f8f9fa',
                },
            },
        },
        cells: {
            style: {
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
    };

    const columns = [
        {
            name: 'Usuario',
            selector: (row) => row.nombre,
            sortable: true,
            cell: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        width: '28px', height: '28px', borderRadius: '4px',
                        backgroundColor: '#1b2a4a',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: '600', fontSize: '0.7rem', flexShrink: 0,
                    }}>
                        {row.nombre?.charAt(0)?.toUpperCase()}
                    </div>
                    <span style={{ fontWeight: '500', fontSize: '0.82rem' }}>{row.nombre}</span>
                </div>
            ),
        },
        {
            name: 'Rol',
            selector: (row) => row.rol,
            sortable: true,
            cell: (row) => (
                <span style={{
                    padding: '2px 8px', borderRadius: '3px', fontSize: '0.72rem',
                    fontWeight: '500', background: '#e9ecef', color: '#495057',
                    border: '1px solid #dee2e6',
                }}>
                    {row.rol}
                </span>
            ),
            width: '120px',
        },
        {
            name: 'Naves Asignadas',
            cell: (row) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', padding: '4px 0' }}>
                    {row.navesArr.length > 0 ? row.navesArr.map((name, i) => (
                        <span key={i} style={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            padding: '1px 7px', borderRadius: '3px', fontSize: '0.7rem',
                            fontWeight: '500', background: '#e7f0fd', color: '#1a5276',
                            border: '1px solid #bee0fc',
                        }}>
                            <i className='fa-solid fa-ship' style={{ fontSize: '0.55rem' }}></i>
                            {name}
                        </span>
                    )) : (
                        <span style={{
                            padding: '1px 7px', borderRadius: '3px', fontSize: '0.7rem',
                            fontWeight: '500', background: '#fdf0ef', color: '#922b21',
                            border: '1px solid #f5c6cb',
                        }}>
                            Sin asignar
                        </span>
                    )}
                </div>
            ),
            wrap: true,
            grow: 2,
        },
        {
            name: '',
            cell: (row) => (
                <button onClick={() => openModal(row.userId)} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '4px 12px', border: '1px solid #1b2a4a', borderRadius: '4px',
                    background: '#1b2a4a', color: '#fff', fontWeight: '500', fontSize: '0.75rem',
                    cursor: 'pointer', transition: 'background 0.15s',
                }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#2c3e6b'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#1b2a4a'; }}
                >
                    <i className='fa-solid fa-pen-to-square' style={{ fontSize: '0.65rem' }}></i> Asignar
                </button>
            ),
            width: '110px',
            right: true,
        },
    ];

    const data = useMemo(() => {
        const activeUsers = usuarios.filter((d) => d.activo === true);
        const filtered = tableFilter.trim()
            ? activeUsers.filter((c) => c.nombre?.toLowerCase().includes(tableFilter.toLowerCase()))
            : activeUsers;

        return filtered.map((c) => {
            const navesArr = asignaciones
                .filter((a) => String(a.userid) === String(c.id))
                .map((a) => {
                    const nave = naves.find((n) => String(n.id) === String(a.activo_id));
                    return nave ? nave.nombre_activo : '';
                })
                .filter(Boolean);

            return { nombre: c.nombre, rol: c.rol, navesArr, userId: c.id };
        });
    }, [usuarios, asignaciones, naves, tableFilter]);

    const selectedUserName = usuarios.find((u) => String(u.id) === String(userSelected))?.nombre || '';

    return (
        <div>
            <AdminSidebar />
            {isModalOpen && (
                <div className='an-overlay' onClick={closeModal}>
                    <div className='an-modal' onClick={(e) => e.stopPropagation()}>
                        <div className='an-modal-header'>
                            <div className='an-modal-header-left'>
                                <h6 className='an-modal-title'>Asignar Naves</h6>
                                <span className='an-modal-user'>{selectedUserName}</span>
                            </div>
                            <div className='an-modal-header-right'>
                                <span className='an-counter'>{selectedNavesIds.length} seleccionadas</span>
                                <button className='an-close-btn' onClick={closeModal}>&times;</button>
                            </div>
                        </div>

                        <div className='an-modal-toolbar'>
                            <div className='an-search-wrap'>
                                <i className='fa-solid fa-magnifying-glass an-search-icon'></i>
                                <input
                                    type='text'
                                    className='an-search-input'
                                    placeholder='Filtrar naves...'
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button className='an-search-clear' onClick={() => setSearchTerm('')}>&times;</button>
                                )}
                            </div>
                            <button className='an-select-all-btn' onClick={handleSelectAll}>
                                {filteredNaves.every((n) => selectedNavesIds.includes(String(n.id)))
                                    ? 'Deseleccionar todo'
                                    : 'Seleccionar todo'}
                            </button>
                        </div>

                        <div className='an-modal-body'>
                            {filteredNaves.length === 0 ? (
                                <div className='an-empty'>
                                    <p>Sin resultados</p>
                                </div>
                            ) : (
                                <div className='an-list'>
                                    {filteredNaves.map((c) => {
                                        const isSelected = selectedNavesIds.includes(String(c.id));
                                        return (
                                            <div
                                                key={c.id}
                                                className={`an-row ${isSelected ? 'an-row-selected' : ''}`}
                                                onClick={() => handleCheckboxChange(String(c.id), !isSelected)}
                                            >
                                                <input type='checkbox' checked={isSelected} readOnly className='an-check' />
                                                <i className='fa-solid fa-ship an-row-icon'></i>
                                                <span className='an-row-name'>{c.nombre_activo}</span>
                                                <span className='an-row-type'>{c.tipo_activo}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className='an-modal-footer'>
                            <button onClick={closeModal} className='an-btn an-btn-cancel'>Cancelar</button>
                            <button onClick={saveValues} className='an-btn an-btn-save'>Guardar</button>
                        </div>
                    </div>
                </div>
            )}
            {loading ? (
                <div className='home'><Loading /></div>
            ) : (
                <div className='home'>
                    <div className='navCentro'>
                        <ol className='breadcrumb'>
                            <li className='breadcrumb-item'><i className='fa-solid fa-users'></i> USUARIO</li>
                            <li className='breadcrumb-item'>Asignar Nave</li>
                        </ol>
                        <div className='row navTab'>
                            <div className='col-md-12 navTabItem'>
                                <Link to='/admin/usuarios'><i className='fa-solid fa-chevron-left mr-2'></i>Atrás</Link>
                            </div>
                        </div>
                    </div>
                    <div className='an-table-wrap'>
                        <div className='an-table-header'>
                            <h6 className='an-table-title'>Asignación de Naves</h6>
                            <div className='an-table-search-wrap'>
                                <i className='fa-solid fa-magnifying-glass an-table-search-icon'></i>
                                <input
                                    type='text'
                                    className='an-table-search'
                                    placeholder='Buscar usuario...'
                                    value={tableFilter}
                                    onChange={(e) => setTableFilter(e.target.value)}
                                />
                            </div>
                        </div>
                        <DataTable
                            columns={columns}
                            data={data}
                            pagination
                            customStyles={tableCustomStyles}
                            paginationComponentOptions={{ rowsPerPageText: 'Filas por página:', rangeSeparatorText: 'de' }}
                            noDataComponent={<div style={{ padding: '40px', color: '#94a3b8' }}>No se encontraron usuarios</div>}
                        />
                    </div>
                </div>
            )}
            <style>{`
                /* ===== TABLE ===== */
                .an-table-wrap {
                    background: #fff;
                    border: 1px solid #dee2e6;
                    border-radius: 6px;
                    overflow: hidden;
                    margin: 0 15px;
                }
                .an-table-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px 16px;
                    border-bottom: 1px solid #dee2e6;
                    background: #f8f9fa;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                .an-table-title {
                    margin: 0;
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #1b2a4a;
                }
                .an-table-search-wrap { position: relative; }
                .an-table-search-icon {
                    position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
                    color: #adb5bd; font-size: 0.7rem;
                }
                .an-table-search {
                    padding: 5px 10px 5px 28px;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    font-size: 0.78rem;
                    outline: none;
                    width: 200px;
                    transition: border-color 0.15s;
                }
                .an-table-search:focus { border-color: #1b2a4a; }

                /* ===== OVERLAY ===== */
                .an-overlay {
                    position: fixed; inset: 0;
                    background: rgba(0,0,0,0.45);
                    display: flex; justify-content: center; align-items: center;
                    z-index: 1050;
                }

                /* ===== MODAL ===== */
                .an-modal {
                    background: #fff;
                    border-radius: 6px;
                    box-shadow: 0 8px 30px rgba(0,0,0,0.18);
                    width: 560px;
                    max-width: 94vw;
                    max-height: 80vh;
                    display: flex;
                    flex-direction: column;
                    animation: an-slideUp 0.2s ease-out;
                }
                @keyframes an-slideUp {
                    from { transform: translateY(20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }

                /* ===== HEADER ===== */
                .an-modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px 16px;
                    border-bottom: 1px solid #dee2e6;
                    background: #1b2a4a;
                    border-radius: 6px 6px 0 0;
                }
                .an-modal-header-left {
                    display: flex; align-items: center; gap: 10px;
                }
                .an-modal-title {
                    margin: 0; font-size: 0.85rem; font-weight: 600; color: #fff;
                }
                .an-modal-user {
                    font-size: 0.75rem; color: rgba(255,255,255,0.7);
                    padding-left: 10px; border-left: 1px solid rgba(255,255,255,0.2);
                }
                .an-modal-header-right {
                    display: flex; align-items: center; gap: 10px;
                }
                .an-counter {
                    font-size: 0.7rem; color: rgba(255,255,255,0.8);
                    background: rgba(255,255,255,0.15);
                    padding: 2px 8px; border-radius: 3px;
                }
                .an-close-btn {
                    background: none; border: none;
                    color: rgba(255,255,255,0.7); font-size: 1.2rem;
                    cursor: pointer; line-height: 1; padding: 0 2px;
                }
                .an-close-btn:hover { color: #fff; }

                /* ===== TOOLBAR ===== */
                .an-modal-toolbar {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 16px;
                    background: #f8f9fa;
                    border-bottom: 1px solid #dee2e6;
                    gap: 8px;
                }
                .an-search-wrap {
                    position: relative; flex: 1; max-width: 260px;
                }
                .an-search-icon {
                    position: absolute; left: 9px; top: 50%; transform: translateY(-50%);
                    color: #adb5bd; font-size: 0.68rem; pointer-events: none;
                }
                .an-search-input {
                    width: 100%; padding: 5px 24px 5px 26px;
                    border: 1px solid #ced4da; border-radius: 4px;
                    font-size: 0.78rem; outline: none; background: #fff;
                }
                .an-search-input:focus { border-color: #1b2a4a; }
                .an-search-clear {
                    position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
                    background: none; border: none; color: #adb5bd; cursor: pointer;
                    font-size: 0.8rem; line-height: 1; padding: 0;
                }
                .an-search-clear:hover { color: #495057; }
                .an-select-all-btn {
                    padding: 4px 10px; border: 1px solid #ced4da; border-radius: 4px;
                    background: #fff; font-size: 0.72rem; font-weight: 500;
                    color: #495057; cursor: pointer; white-space: nowrap;
                }
                .an-select-all-btn:hover { background: #e9ecef; }

                /* ===== BODY ===== */
                .an-modal-body {
                    flex: 1; overflow-y: auto; padding: 0;
                }
                .an-modal-body::-webkit-scrollbar { width: 5px; }
                .an-modal-body::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 3px; }

                .an-empty {
                    display: flex; align-items: center; justify-content: center;
                    padding: 30px 0; color: #adb5bd;
                }
                .an-empty p { margin: 0; font-size: 0.82rem; }

                /* ===== LIST ROWS ===== */
                .an-list { display: flex; flex-direction: column; }
                .an-row {
                    display: flex; align-items: center; gap: 10px;
                    padding: 7px 16px;
                    border-bottom: 1px solid #f1f3f5;
                    cursor: pointer; user-select: none;
                    transition: background 0.1s;
                    font-size: 0.8rem;
                }
                .an-row:last-child { border-bottom: none; }
                .an-row:hover { background: #f8f9fa; }
                .an-row-selected { background: #edf2fb; }
                .an-row-selected:hover { background: #e2ebf6; }

                .an-check {
                    width: 15px; height: 15px; flex-shrink: 0;
                    accent-color: #1b2a4a; cursor: pointer;
                }
                .an-row-icon {
                    font-size: 0.7rem; color: #6c757d; flex-shrink: 0;
                }
                .an-row-selected .an-row-icon { color: #1b2a4a; }
                .an-row-name {
                    font-weight: 500; color: #212529; flex: 1;
                    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }
                .an-row-type {
                    font-size: 0.7rem; color: #adb5bd; flex-shrink: 0;
                }

                /* ===== FOOTER ===== */
                .an-modal-footer {
                    display: flex; justify-content: flex-end; gap: 8px;
                    padding: 10px 16px;
                    border-top: 1px solid #dee2e6;
                    background: #f8f9fa;
                    border-radius: 0 0 6px 6px;
                }
                .an-btn {
                    padding: 5px 16px; border-radius: 4px;
                    font-size: 0.78rem; font-weight: 500;
                    cursor: pointer; border: 1px solid transparent;
                }
                .an-btn-cancel {
                    background: #fff; color: #495057; border-color: #ced4da;
                }
                .an-btn-cancel:hover { background: #e9ecef; }
                .an-btn-save {
                    background: #1b2a4a; color: #fff; border-color: #1b2a4a;
                }
                .an-btn-save:hover { background: #2c3e6b; }

                @media (max-width: 640px) {
                    .an-modal { width: 96vw; }
                    .an-table-header { flex-direction: column; align-items: flex-start; }
                    .an-table-search { width: 100%; }
                    .an-modal-toolbar { flex-direction: column; align-items: stretch; }
                    .an-search-wrap { max-width: 100%; }
                }
            `}</style>
        </div>
    );
};

export default AsignarNave;